import { Injectable, inject, signal } from '@angular/core';
import { AppKit, createAppKit } from '@reown/appkit';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { mainnet, sepolia } from '@reown/appkit/networks';
import { getAccount, watchAccount, reconnect } from '@wagmi/core';
import { Router } from '@angular/router';
import { config } from '../../configs/wagmi-config';
import { environment } from '../../../../environments/environment';
import { LocalStorageService } from './local-storage.service';
import { WalletSessionSecurityService } from './wallet-session-security.service';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AppkitService {
  private router = inject(Router);
  private localStorageService = inject(LocalStorageService);
  private walletSessionSecurity = inject(WalletSessionSecurityService);

  private modal: AppKit | null = null;
  private appKitReady: Promise<void>;
  private walletState = signal<{ account: string; chainId: number; connected: boolean } | null>(null);
  private wagmiConfig = config;
  wagmiAdapter!: WagmiAdapter;
  private unwatchAccount: (() => void) | null = null;
  private cleanupEthereumListeners: (() => void) | null = null;
  private walletConsistencyPoller: ReturnType<typeof setInterval> | null = null;
  private walletInvalidationSub: Subscription | null = null;
  constructor() {
    this.appKitReady = this.initializeAppKit();
  }

  private async initializeAppKit() {
    try {
      const projectId = environment.PROJECT_ID;
      const metadata = {
        name: 'app-aqualux',
        description: 'AppKit Example',
        url: 'https://reown.com/appkit',
        icons: ['https://assets.reown.com/reown-profile-pic.png'],
      };

      const activeChain = environment.production ? mainnet : sepolia;

      this.wagmiAdapter = new WagmiAdapter({
        projectId,
        networks: [activeChain],
      });

      this.modal = createAppKit({
        adapters: [this.wagmiAdapter],
        networks: [activeChain],
        projectId,
        metadata,
        features: {
          analytics: true,
          email: false,
          socials: false,
        },
        allWallets: 'SHOW',
      });

      this.wagmiConfig = this.wagmiAdapter.wagmiConfig as any;
      this.setConfig(this.wagmiAdapter);

      try {
        await reconnect(this.wagmiConfig as any);
      } catch (e) {
        console.warn('Wagmi reconnect failed during initialization', e);
      }

      await this.updateWalletInfo();
      this.watchWalletConnection();
      this.listenModalEvents();
      this.listenEthereumEvents();
      this.startWalletConsistencyPoller();
      this.listenForSessionInvalidation();
    } catch (error) {
      console.error('AppKit initialization failed:', error);
      throw error;
    }
  }


  /**
* Set configuration
* @param config 
*/
  setConfig(config: WagmiAdapter) {
    this.wagmiAdapter = config;
  }

  /**
   * get config
   * @returns 
   */
  fetchConfig() {
    if (!this.wagmiAdapter?.wagmiConfig) {
      console.warn('AppKit is not initialized correctly. Falling back to default wagmiConfig.');
      return this.wagmiConfig;
    }
    return this.wagmiAdapter.wagmiConfig;
  }

  async listenModalEvents() {
    if (!this.modal) return;

    // Future Use
    this.modal.subscribeEvents((event: any) => {
      this.updateWalletInfo();
    });

    this.modal.subscribeAccount((account: any) => {
      this.updateWalletInfo();
    })
    // Future Use
    // this.modal.subscribeAccount(...)
    // this.modal.subscribeState(...)
  }

  /*
  * Listen to Ethereum events for account changes and disconnections
  */
  private listenEthereumEvents() {
    this.cleanupEthereumListeners?.();
    this.cleanupEthereumListeners = null;

    if (typeof window === 'undefined') {
      return;
    }

    const ethereum = (window as Window & { ethereum?: any }).ethereum;
    if (!ethereum?.on || !ethereum?.removeListener) {
      return;
    }

    // Handle account changes and disconnections
    const handleAccountsChanged = (accounts: string[]) => {
      if (!accounts || accounts.length === 0) {
        const current = this.walletState();
        this.walletState.set(null);
        if (current?.connected && !this.walletSessionSecurity.isInvalidationInProgress()) {
          void this.walletSessionSecurity.forceSessionInvalidation('Wallet disconnected.');
        }
        return;
      }

      const connectedAccount = String(accounts[0] ?? '');
      const current = this.walletState();

      if (current?.account && current.account.toLowerCase() !== connectedAccount.toLowerCase()) {
        this.walletState.set(null);
        if (!this.walletSessionSecurity.isInvalidationInProgress()) {
          void this.walletSessionSecurity.forceSessionInvalidation('Wallet changed. Please reconnect.');
        }
      }

      void this.updateWalletInfo();
    };

    /*
    * Handle wallet disconnection
    */
    const handleDisconnect = () => {
      // On explicit disconnect, we clear state without showing a toast to avoid duplicate messages during account switches.
      const current = this.walletState();
      if (current?.connected && !this.walletSessionSecurity.isInvalidationInProgress()) {
        void this.walletSessionSecurity.forceSessionInvalidation('Wallet disconnected.');
      }
      this.walletState.set(null);
    };

    ethereum.on('accountsChanged', handleAccountsChanged);
    ethereum.on('disconnect', handleDisconnect);

    /*
    * Cleanup function to remove event listeners
    */
    this.cleanupEthereumListeners = () => {
      ethereum.removeListener('accountsChanged', handleAccountsChanged);
      ethereum.removeListener('disconnect', handleDisconnect);
    };
  }

  /*
  * Start a poller to check wallet consistency every 750ms
  */
  private startWalletConsistencyPoller() {
    if (this.walletConsistencyPoller || typeof window === 'undefined') {
      return;
    }

    this.walletConsistencyPoller = setInterval(() => {
      const token = this.localStorageService.getToken();
      const authenticatedWallet = this.localStorageService.getAuthenticatedWallet();

      if (!token && !authenticatedWallet) {
        return;
      }

      const connection = getAccount(this.wagmiConfig as any);
      if (connection.isReconnecting || connection.isConnecting) {
        return;
      }
      const account = connection.address ? String(connection.address) : '';

      if (account && authenticatedWallet && account.toLowerCase() !== authenticatedWallet.toLowerCase()) {
        void this.walletSessionSecurity.forceSessionInvalidation('Wallet changed. Please reconnect.');
      }
    }, 750);
  }

  /*
  * Stop the wallet consistency poller
  */
  private stopWalletConsistencyPoller() {
    if (this.walletConsistencyPoller) {
      clearInterval(this.walletConsistencyPoller);
      this.walletConsistencyPoller = null;
    }
  }

  /*
  * Watch for wallet connection changes
  */
  private watchWalletConnection() {
    if (this.unwatchAccount) {
      this.unwatchAccount();
      this.unwatchAccount = null;
    }

    this.unwatchAccount = watchAccount(this.wagmiConfig as any, {
      onChange: () => {
        void this.updateWalletInfo();
      },
    });
  }

  /*
  * Public method to check if the AppKit is ready
  */
  public ready(): Promise<void> {
    return this.appKitReady;
  }

  /* 
  * Listen for session invalidation events and handle wallet disconnection
  */
  private listenForSessionInvalidation() {
    this.walletInvalidationSub?.unsubscribe();
    this.walletInvalidationSub = this.walletSessionSecurity.sessionInvalidation$.subscribe(() => {
      this.stopWalletConsistencyPoller();
      void this.silentDisconnectWallet();
    });
  }



  // async switchNetwork() {
  //   if (!this.modal) return;
  //     await this.modal.switchNetwork({
  //     chainId: config.chains, // Ethereum Mainnet
  //   } as any);
  //   this.updateWalletInfo();
  // }


  /*
  * Wait for Wagmi connection to resolve if it is currently reconnecting or connecting
  */
  async waitForConnectionResolve(): Promise<void> {
    const maxRetries = 40; // 40 * 50ms = 2000ms max wait
    let retries = 0;
    let connection = getAccount(this.wagmiConfig as any);
    
    while ((connection.isReconnecting || connection.isConnecting) && retries < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 50));
      connection = getAccount(this.wagmiConfig as any);
      retries++;
    }
    
    await this.updateWalletInfo();
  }

  /*
  * Update wallet information and enforce consistency
  */
  async updateWalletInfo() {
    const connection = getAccount(this.wagmiConfig as any);
    if (connection.isReconnecting || connection.isConnecting) {
      return;
    }
    const account = connection.address ? String(connection.address) : '';
    const chainId =
      typeof connection.chainId === 'number'
        ? connection.chainId
        : this.walletState()?.chainId ?? environment.SUPPORTED_NETWORK[0].id;

    if (account) {
      this.walletState.set({ account, chainId, connected: true });
      this.startWalletConsistencyPoller();
      void this.enforceWalletConsistency(account);
      return;
    }

    const currentState = this.walletState();
    if (currentState?.connected && !this.walletSessionSecurity.isInvalidationInProgress()) {
      void this.walletSessionSecurity.forceSessionInvalidation('Wallet disconnected.');
    }

    this.walletState.set(null);
  }

  /*
  * Enforce wallet consistency by checking if the connected wallet matches the authenticated wallet
  */
  private async enforceWalletConsistency(connectedWallet: string) {
    const authenticatedWallet = this.localStorageService.getAuthenticatedWallet();
    const token = this.localStorageService.getToken();

    if (!token || !authenticatedWallet) {
      return;
    }

    if (authenticatedWallet.toLowerCase() !== connectedWallet.toLowerCase()) {
      await this.walletSessionSecurity.forceSessionInvalidation('Wallet changed. Please reconnect.');
    }
  }

  /*
  * Get the wallet signal for reactive updates
  */
  getWalletSignal() {
    // asReadonly is used to make the signal immutable
    // is  set the value in the walletState signal triggers a re-render 
    return this.walletState.asReadonly(); // Use in Navbar
  }

  /*
  * Get the current wallet information
  */
  getCurrentWallet() {
    return this.walletState();
  }

  /*
  * Check if the wallet is connected
  */
  public openConnectModal() {
    if (!this.modal) {
      void this.appKitReady.then(() => this.modal?.open({ view: 'Connect' }));
      return;
    }
    this.modal?.open({ view: 'Connect' });
  }

  /* 
  * Open the network selection modal
  */
  public openNetworkModal() {
    this.modal?.open({ view: 'Networks' });
  }

  public getWalletInfo() {
    return this.modal?.getWalletInfo();
  }

  async disconnect() {
    try {
      await this.disconnectWalletOnly();

      const onLandingPage = this.router.url === '/' || this.router.url.startsWith('/coming-soon') || this.router.url === '/membership' || this.router.url === '/wellness';
      const message = onLandingPage ? 'Wallet disconnected.' : 'Wallet Disconnected.';
      await this.walletSessionSecurity.forceSessionInvalidation(message);
    } catch (error) {
      console.error('Disconnect failed:', error);
    }
  }

  /*
  * Disconnect the wallet without invalidating the session
  */
  async disconnectWalletOnly() {
    try {
      if (this.modal) {
        await this.modal.disconnect();
      }
      this.walletState.set(null);
      this.stopWalletConsistencyPoller();
    } catch (error) {
      console.error('Disconnect wallet only failed:', error);
      await this.updateWalletInfo();
    }
  }

  private async silentDisconnectWallet() {
    try {
      if (this.modal) {
        await this.modal.disconnect();
      }
      this.walletState.set(null);
    } catch (error) {
      console.error('Silent wallet disconnect failed:', error);
      await this.updateWalletInfo();
    }
  }

  public triggerWalletUpdate() {
    this.updateWalletInfo();
  }
}